/**
 * 衝突計算服務 | Conflict Calculator Service
 *
 * 負責計算插單與現有工單之間的衝突
 * Calculates conflicts between rush orders and existing work orders
 */

import { RushOrder } from '@/types/person';
import { MainWorkOrder, SubWorkOrder, ConflictResult, DelayDecision } from '@/types/order';
import { Product } from '@/types/person';

/**
 * 時間範圍介面 | Time Range Interface
 */
interface TimeRange {
  start: Date;
  end: Date;
}

/**
 * 計算插單的時間範圍
 * Calculate the time range for a rush order
 *
 * @param rushOrder - 插單資料 | Rush order data
 * @param product - 產品資料（用於獲取標準工時）| Product data (for standard hours)
 * @returns 時間範圍 | Time range
 */
export function calculateRushOrderTimeRange(
  rushOrder: RushOrder,
  product?: Product
): TimeRange {
  const start = new Date(rushOrder.requestedDeliveryDate);
  const end = new Date(start);

  // 如果有產品資料，使用產品的標準工時；否則預設 30 天
  // Use product's standard hours if available; otherwise default to 30 days
  const durationDays = product?.standardHours
    ? Math.ceil(product.standardHours / 24)  // 工時轉天數 | Convert hours to days
    : 30;

  end.setDate(end.getDate() + durationDays);

  return { start, end };
}

/**
 * 檢查兩個時間範圍是否重疊
 * Check if two time ranges overlap
 *
 * 核心公式：start1 < end2 && end1 > start2
 * Core formula: start1 < end2 && end1 > start2
 *
 * @param range1 - 時間範圍 1 | Time range 1
 * @param range2 - 時間範圍 2 | Time range 2
 * @returns 是否重疊 | Whether they overlap
 */
export function checkTimeOverlap(range1: TimeRange, range2: TimeRange): boolean {
  return range1.start < range2.end && range1.end > range2.start;
}

/**
 * 檢查資源衝突
 * Check for resource conflicts
 *
 * @param subOrders1 - 工單 1 的子工單 | Sub work orders of work order 1
 * @param subOrders2 - 工單 2 的子工單 | Sub work orders of work order 2
 * @returns 衝突的資源 | Conflicting resources
 */
export function checkResourceConflict(
  subOrders1: SubWorkOrder[],
  subOrders2: SubWorkOrder[]
): {
  persons: string[];
  machines: string[];
  materials: string[];
} {
  const conflicts = {
    persons: [] as string[],
    machines: [] as string[],
    materials: [] as string[],
  };

  // 收集工單 1 的所有資源
  const resources1 = {
    persons: new Set<string>(),
    machines: new Set<string>(),
    materials: new Set<string>(),
  };

  subOrders1.forEach((swo) => {
    swo.assignedPersons.forEach((p) => resources1.persons.add(p));
    if (swo.machineId) resources1.machines.add(swo.machineId);
    swo.requiredMaterials.forEach((m) => resources1.materials.add(m));
  });

  // 檢查工單 2 是否有重疊資源
  subOrders2.forEach((swo) => {
    swo.assignedPersons.forEach((p) => {
      if (resources1.persons.has(p) && !conflicts.persons.includes(p)) {
        conflicts.persons.push(p);
      }
    });
    if (swo.machineId && resources1.machines.has(swo.machineId) && !conflicts.machines.includes(swo.machineId)) {
      conflicts.machines.push(swo.machineId);
    }
    swo.requiredMaterials.forEach((m) => {
      if (resources1.materials.has(m) && !conflicts.materials.includes(m)) {
        conflicts.materials.push(m);
      }
    });
  });

  return conflicts;
}

/**
 * 計算單一工單的延後天數
 * Calculate delay days for a single work order
 *
 * @param conflictingWorkOrder - 衝突工單 | Conflicting work order
 * @param rushOrderTimeRange - 插單時間範圍 | Rush order time range
 * @returns 延後天數 | Delay days
 */
export function calculateDelayDays(
  conflictingWorkOrder: MainWorkOrder,
  rushOrderTimeRange: TimeRange
): number {
  const conflictStart = new Date(conflictingWorkOrder.plannedStart);
  const rushEnd = rushOrderTimeRange.end;

  // 如果插單結束時間晚於衝突工單開始時間，需要延後
  // If rush order ends after conflict starts, need to delay
  if (rushEnd > conflictStart) {
    const diffTime = rushEnd.getTime() - conflictStart.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  return 0;
}

/**
 * 計算延後後的新時間
 * Calculate new time after delay
 *
 * @param originalStart - 原開始時間 | Original start time
 * @param originalEnd - 原結束時間 | Original end time
 * @param delayDays - 延後天數 | Delay days
 * @returns 新的開始和結束時間 | New start and end times
 */
export function calculateRescheduledTime(
  originalStart: string,
  originalEnd: string,
  delayDays: number
): { newStart: string; newEnd: string } {
  const startDate = new Date(originalStart);
  const endDate = new Date(originalEnd);

  startDate.setDate(startDate.getDate() + delayDays);
  endDate.setDate(endDate.getDate() + delayDays);

  return {
    newStart: startDate.toISOString(),
    newEnd: endDate.toISOString(),
  };
}

/**
 * 計算與插單衝突的所有工單
 * Calculate all work orders that conflict with the rush order
 *
 * @param rushOrder - 插單資料 | Rush order data
 * @param existingOrders - 現有工單列表 | List of existing work orders
 * @param subOrdersMap - 子工單映射（key: mainOrderId）| Sub work orders map (key: mainOrderId)
 * @param productsMap - 產品映射（key: productId）| Products map (key: productId)
 * @returns 衝突結果列表（按優先級排序）| List of conflict results (sorted by priority)
 */
export function calculateConflicts(
  rushOrder: RushOrder,
  existingOrders: MainWorkOrder[],
  subOrdersMap: Map<string, SubWorkOrder[]>,
  productsMap?: Map<string, Product>
): ConflictResult[] {
  const results: ConflictResult[] = [];

  // 1. 獲取產品資料用於計算時間範圍
  // Get product data for time range calculation
  const product = productsMap?.get(rushOrder.productId);

  // 2. 計算插單的時間範圍
  // Calculate rush order time range
  const rushTimeRange = calculateRushOrderTimeRange(rushOrder, product);

  // 3. 檢查每個現有工單是否與插單衝突
  // Check each existing work order for conflicts
  existingOrders.forEach((mwo) => {
    const mwoStart = new Date(mwo.plannedStart);
    const mwoEnd = new Date(mwo.plannedEnd);

    // 檢查時間重疊
    // Check time overlap
    const hasTimeOverlap = checkTimeOverlap(
      { start: mwoStart, end: mwoEnd },
      rushTimeRange
    );

    if (!hasTimeOverlap) {
      return; // 沒有時間衝突，跳過 | No time conflict, skip
    }

    // 4. 檢查資源衝突
    // Check resource conflicts
    const mwoSubOrders = subOrdersMap.get(mwo.id) || [];
    const rushSubOrders = subOrdersMap.get(rushOrder.id) || [];

    const resourceConflicts = checkResourceConflict(mwoSubOrders, rushSubOrders);

    // 5. 計算延後天數和新時間
    // Calculate delay days and new time
    const delayDays = calculateDelayDays(mwo, rushTimeRange);
    const rescheduledTime = calculateRescheduledTime(
      mwo.plannedStart,
      mwo.plannedEnd,
      delayDays
    );

    // 6. 判斷衝突類型
    // Determine conflict type
    const hasResourceConflict =
      resourceConflicts.persons.length > 0 ||
      resourceConflicts.machines.length > 0 ||
      resourceConflicts.materials.length > 0;

    const conflictType: 'resource' | 'time' | 'both' = hasResourceConflict
      ? 'both'
      : 'time';

    // 7. 建立衝突結果
    // Create conflict result
    const conflictResult: ConflictResult = {
      workOrderId: mwo.id,
      workOrderName: `${mwo.workflowId}-${mwo.id}`,
      orderId: mwo.orderId,
      orderProductName: rushOrder.productName, // 使用插單的產品名稱
      customer: rushOrder.customer,
      originalPlannedStart: mwo.plannedStart,
      originalPlannedEnd: mwo.plannedEnd,
      estimatedDelayStart: rescheduledTime.newStart,
      estimatedDelayEnd: rescheduledTime.newEnd,
      delayDays,
      conflictReason: buildConflictReason(conflictType, delayDays, resourceConflicts),
      conflictType,
      affectedResources: resourceConflicts,
      isReviewed: false,
      isAccepted: false,
    };

    results.push(conflictResult);
  });

  // 8. 按優先級排序（延後天數少的優先）
  // Sort by priority (less delay first)
  results.sort((a, b) => a.delayDays - b.delayDays);

  return results;
}

/**
 * 建立衝突原因描述
 * Build conflict reason description
 *
 * @param conflictType - 衝突類型 | Conflict type
 * @param delayDays - 延後天數 | Delay days
 * @param resources - 衝突資源 | Conflicting resources
 * @returns 衝突原因描述 | Conflict reason description
 */
function buildConflictReason(
  conflictType: 'resource' | 'time' | 'both',
  delayDays: number,
  resources: { persons: string[]; machines: string[]; materials: string[] }
): string {
  const parts: string[] = [];

  if (conflictType === 'time' || conflictType === 'both') {
    parts.push(`時間衝突，需延後 ${delayDays} 天`);
  }

  if (conflictType === 'resource' || conflictType === 'both') {
    const resourceParts: string[] = [];
    if (resources.persons.length > 0) {
      resourceParts.push(`${resources.persons.length} 個人員`);
    }
    if (resources.machines.length > 0) {
      resourceParts.push(`${resources.machines.length} 台機台`);
    }
    if (resources.materials.length > 0) {
      resourceParts.push(`${resources.materials.length} 種物料`);
    }
    if (resourceParts.length > 0) {
      parts.push(`資源衝突（${resourceParts.join('、')}）`);
    }
  }

  return parts.join('，') || '未知衝突';
}

/**
 * 從衝突結果建立延後決策
 * Create delay decision from conflict result
 *
 * @param conflict - 衝突結果 | Conflict result
 * @param rushOrderId - 插單 ID | Rush order ID
 * @returns 延後決策 | Delay decision
 */
export function createDelayDecision(
  conflict: ConflictResult,
  rushOrderId: string
): DelayDecision {
  return {
    workOrderId: conflict.workOrderId,
    workOrderName: conflict.workOrderName,
    originalPlannedStart: conflict.originalPlannedStart,
    originalPlannedEnd: conflict.originalPlannedEnd,
    newPlannedStart: conflict.estimatedDelayStart,
    newPlannedEnd: conflict.estimatedDelayEnd,
    delayDays: conflict.delayDays,
    reason: rushOrderId,
    timestamp: new Date().toISOString(),
    priority: conflict.delayDays, // 延後天數作為優先級 | Delay days as priority
  };
}

/**
 * 重新計算剩餘衝突（在用戶接受一個延後後）
 * Recalculate remaining conflicts (after user accepts a delay)
 *
 * @param remainingConflicts - 尚未審核的衝突 | Remaining unreviewed conflicts
 * @param acceptedDelay - 已接受的延後決策 | Accepted delay decision
 * @param existingOrders - 現有工單列表 | List of existing work orders
 * @returns 更新後的衝突列表 | Updated conflict list
 */
export function recalculateConflicts(
  remainingConflicts: ConflictResult[],
  acceptedDelay: DelayDecision,
  existingOrders: MainWorkOrder[]
): ConflictResult[] {
  // 找到被延後的工單
  // Find the delayed work order
  const delayedOrder = existingOrders.find((o) => o.id === acceptedDelay.workOrderId);
  if (!delayedOrder) {
    return remainingConflicts;
  }

  // 更新該工單的計畫時間（僅用於後續衝突計算）
  // Update planned time for that work order (only for subsequent calculations)
  const updatedOrder: MainWorkOrder = {
    ...delayedOrder,
    plannedStart: acceptedDelay.newPlannedStart,
    plannedEnd: acceptedDelay.newPlannedEnd,
  };

  // 更新 remaining conflicts 中受影響的工單
  // Update affected work orders in remaining conflicts
  return remainingConflicts.map((conflict) => {
    const conflictOrder = existingOrders.find((o) => o.id === conflict.workOrderId);
    if (!conflictOrder) {
      return conflict;
    }

    // 檢查是否與被延後的工單有新的時間重疊
    // Check if there's new time overlap with delayed order
    const updatedConflictStart = new Date(updatedOrder.plannedStart);
    const updatedConflictEnd = new Date(updatedOrder.plannedEnd);
    const currentStart = new Date(conflictOrder.plannedStart);
    const currentEnd = new Date(conflictOrder.plannedEnd);

    const hasNewOverlap = checkTimeOverlap(
      { start: updatedConflictStart, end: updatedConflictEnd },
      { start: currentStart, end: currentEnd }
    );

    if (!hasNewOverlap) {
      return conflict; // 沒有新的重疊，保持原樣 | No new overlap, keep as is
    }

    // 計算新的延後天數
    // Calculate new delay days
    const newDelayDays = calculateDelayDays(
      conflictOrder,
      { start: updatedConflictStart, end: updatedConflictEnd }
    );

    // 更新衝突結果
    // Update conflict result
    const rescheduledTime = calculateRescheduledTime(
      conflictOrder.plannedStart,
      conflictOrder.plannedEnd,
      newDelayDays
    );

    return {
      ...conflict,
      estimatedDelayStart: rescheduledTime.newStart,
      estimatedDelayEnd: rescheduledTime.newEnd,
      delayDays: newDelayDays,
      conflictReason: buildConflictReason(
        conflict.conflictType,
        newDelayDays,
        conflict.affectedResources
      ),
    };
  });
}
